import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '../auth/AuthService';
import { Router } from '@angular/router';
import { Observable, concat, defer, forkJoin, from, of, throwError } from 'rxjs';
import { catchError, map, switchMap, toArray } from 'rxjs/operators';
import { jwtDecode } from 'jwt-decode';
import { environment } from '../../environments/environment';

export interface AuditSeverityTag {
  id: number;
  clientId: number;
  name: string;
  active: boolean;
  deductionPercent: number;
  failureCount: number | null;
}

export interface AuditSeverityCreatePayload {
  name: string;
  deductionPercent: number;
  failureCount: number | null;
}

export interface AuditSeverityUpdatePayload extends AuditSeverityCreatePayload {
  active: boolean;
}

export interface AuditErrorType {
  id: number;
  clientId: number;
  name: string;
  active: boolean;
}

export interface AuditErrorTypeCreatePayload {
  name: string;
}

export interface AuditErrorTypeUpdatePayload extends AuditErrorTypeCreatePayload {
  active: boolean;
}

export interface AuditErrorCategory {
  id: number;
  clientId: number;
  name: string;
  active: boolean;
  defaultErrorTypeId: number | null;
  defaultErrorTypeName: string | null;
}

export interface AuditErrorCategoryCreatePayload {
  name: string;
  defaultErrorTypeId: number | null;
}

export interface AuditErrorCategoryUpdatePayload extends AuditErrorCategoryCreatePayload {
  active: boolean;
}

export function resolveQuestionErrorTypeId(
  category: Pick<AuditErrorCategory, 'defaultErrorTypeId'> | null | undefined,
  selectedErrorTypeId: number | null | undefined
): number | null {
  if (category?.defaultErrorTypeId !== null && category?.defaultErrorTypeId !== undefined) {
    return Number(category.defaultErrorTypeId);
  }

  return selectedErrorTypeId === null || selectedErrorTypeId === undefined || selectedErrorTypeId === ('' as any)
    ? null
    : Number(selectedErrorTypeId);
}

export interface QuestionnairePayload {
  officeIds: number[];
  formType: string;
  planCreation: string;
  domain?: 'DENTAL' | 'MEDICAL';
  categoryWeights: { [category: string]: number };
  question: Array<{
    id: number;
    category: string;
    question: string;
    questionCode: string;
    severity: string;
    errorCategoryId: number;
    errorTypeId: number;
  }>;
}

export interface QuestionnaireSaveResult {
  officeIds: number[];
  planCreation: string;
  success: boolean;
  error?: any;
}

export function severityKey(name: string): string {
  return (name || '').trim().toUpperCase().replace(/[\s_-]+/g, '');
}

export function severityLabel(name: string): string {
  const key = severityKey(name);
  return key === 'CRITICAL' ? 'Critical' : key === 'NONCRITICAL' ? 'Non-critical' : name;
}

// An unsuccessful or unrecognised response must never be treated as a new configuration.
export function configurationQuestions(response: any): any[] {
  if (Array.isArray(response)) { return response; }
  if (response === null || response === undefined) { return []; }
  if (Array.isArray(response.question)) { return response.question; }
  throw new Error('Unexpected questionnaire response. Reload before saving.');
}

@Injectable({ providedIn: 'root' })
export class QuestionsService {
  constructor(private httpClient: HttpClient,
              private authService: AuthService,
              private router: Router) { }

  getquestions(params?: any): Observable<any> {
    return this.authService.getP(`${environment.coverageService}question`, params);
  }
  submitQuestionsData(body?: any, params?: any): Observable<any> {
    return this.authService.postP(`${environment.coverageService}office-question`, body, params);
  }
  updateQuestionsData(body?: any, params?: any): Observable<any> {
    return this.authService.put(`${environment.coverageService}office-question`, body, params);
  }
  getConfiguredQuestionnaire(params: any): Observable<any[]> {
    return this.authService.getP(`${environment.coverageService}office-question`, params)
      .pipe(map(configurationQuestions));
  }
  getCategoryDropdown(params?: any): Observable<any> {
    return this.authService.getP(`${environment.coverageService}category`, params);
  }
  getQuestionCategoryList(params?: any): Observable<any> {
    return this.authService.getP(`${environment.coverageService}question`, params);
  }
  getQuestionById(params?: any): Observable<any> {
    return this.authService.getP(`${environment.coverageService}question`, params);
  }
  deleteQuestion(params?: any): Observable<any> {
    return this.authService.delete(`${environment.coverageService}question`, params);
  }
  submitQuestions(body?: any, params?: any): Observable<any> {
    return this.authService.postP(`${environment.coverageService}question`, body, params);
  }
  updateQuestions(body?: any, params?: any): Observable<any> {
    return this.authService.put(`${environment.coverageService}question`, body, params);
  }

  private auditClientId(): Observable<number> {
    return defer(() => {
      const selected = this.authService.getClientId();
      if (Number.isInteger(selected) && selected > 0) { return of(selected); }
      return from(this.authService.getAccessToken()).pipe(map(token => {
        const decoded: any = token ? jwtDecode(token) : {};
        const id = Number(decoded.client_id);
        if (!Number.isInteger(id) || id <= 0) {
          throw new Error('Select a client before configuring question severity.');
        }
        return id;
      }));
    });
  }

  getSeverityTags(): Observable<AuditSeverityTag[]> {
    return this.auditClientId().pipe(switchMap(clientId =>
      this.authService.getP(`${environment.coverageService}audit/severity-tags`, { clientId })
    ), map(response => {
      if (!Array.isArray(response)) { throw new Error('Unexpected severity response. Please retry.'); }
      return response;
    }));
  }

  createSeverityTag(body: AuditSeverityCreatePayload): Observable<AuditSeverityTag> {
    const payload: AuditSeverityCreatePayload = {
      name: (body.name || '').trim(),
      deductionPercent: Number(body.deductionPercent),
      failureCount: body.failureCount === null || body.failureCount === undefined
        ? null
        : Number(body.failureCount)
    };

    return this.auditClientId().pipe(switchMap(clientId =>
      this.authService.postP(
        `${environment.coverageService}audit/severity-tags`,
        payload,
        { clientId }
      )
    ));
  }

  updateSeverityTag(id: number, body: AuditSeverityUpdatePayload): Observable<AuditSeverityTag> {
    const payload: AuditSeverityUpdatePayload = {
      name: (body.name || '').trim(),
      active: Boolean(body.active),
      deductionPercent: Number(body.deductionPercent),
      failureCount: body.failureCount === null || body.failureCount === undefined
        ? null
        : Number(body.failureCount)
    };

    return this.auditClientId().pipe(switchMap(clientId =>
      this.authService.put(
        `${environment.coverageService}audit/severity-tags/${id}`,
        payload,
        { clientId }
      )
    ));
  }


  getErrorTypes(): Observable<AuditErrorType[]> {
    return this.auditClientId().pipe(switchMap(clientId =>
      this.authService.getP(`${environment.coverageService}audit/error-types`, { clientId })
    ), map(response => {
      if (!Array.isArray(response)) { throw new Error('Unexpected error type response. Please retry.'); }
      return response;
    }));
  }

  createErrorType(body: AuditErrorTypeCreatePayload): Observable<AuditErrorType> {
    const payload: AuditErrorTypeCreatePayload = { name: (body.name || '').trim() };
    return this.auditClientId().pipe(switchMap(clientId =>
      this.authService.postP(
        `${environment.coverageService}audit/error-types`,
        payload,
        { clientId }
      )
    ));
  }

  updateErrorType(id: number, body: AuditErrorTypeUpdatePayload): Observable<AuditErrorType> {
    const payload: AuditErrorTypeUpdatePayload = {
      name: (body.name || '').trim(),
      active: Boolean(body.active)
    };
    return this.auditClientId().pipe(switchMap(clientId =>
      this.authService.put(
        `${environment.coverageService}audit/error-types/${id}`,
        payload,
        { clientId }
      )
    ));
  }

  getErrorCategories(): Observable<AuditErrorCategory[]> {
    return this.auditClientId().pipe(switchMap(clientId =>
      this.authService.getP(`${environment.coverageService}audit/error-categories`, { clientId })
    ), map(response => {
      if (!Array.isArray(response)) { throw new Error('Unexpected error category response. Please retry.'); }
      return response;
    }));
  }

  createErrorCategory(body: AuditErrorCategoryCreatePayload): Observable<AuditErrorCategory> {
    const payload: AuditErrorCategoryCreatePayload = {
      name: (body.name || '').trim(),
      defaultErrorTypeId: body.defaultErrorTypeId === null || body.defaultErrorTypeId === undefined
        ? null
        : Number(body.defaultErrorTypeId)
    };
    return this.auditClientId().pipe(switchMap(clientId =>
      this.authService.postP(
        `${environment.coverageService}audit/error-categories`,
        payload,
        { clientId }
      )
    ));
  }

  updateErrorCategory(id: number, body: AuditErrorCategoryUpdatePayload): Observable<AuditErrorCategory> {
    const payload: AuditErrorCategoryUpdatePayload = {
      name: (body.name || '').trim(),
      active: Boolean(body.active),
      defaultErrorTypeId: body.defaultErrorTypeId === null || body.defaultErrorTypeId === undefined
        ? null
        : Number(body.defaultErrorTypeId)
    };
    return this.auditClientId().pipe(switchMap(clientId =>
      this.authService.put(
        `${environment.coverageService}audit/error-categories/${id}`,
        payload,
        { clientId }
      )
    ));
  }

  // Office IDs are singular on GET, but batched on POST/PUT. Resolve each target
  // first so bulk edits and BOTH work even when only some configurations exist.
  saveQuestionnaire(body: QuestionnairePayload): Observable<QuestionnaireSaveResult[]> {
    const plans = body.planCreation === 'BOTH' ? ['YES', 'NO'] : [body.planCreation];
    if (!body.officeIds.length || plans.some(plan => plan !== 'YES' && plan !== 'NO')) {
      return throwError(new Error('Select valid offices and plan creation before saving.'));
    }
    const targets: Array<{ officeId: number; planCreation: string }> = [];
    plans.forEach(planCreation => body.officeIds.forEach(officeId => targets.push({ officeId, planCreation })));
    return forkJoin(targets.map(target => this.getConfiguredQuestionnaire({
      officeId: target.officeId, formType: body.formType, planCreation: target.planCreation, domain: body.domain
    }).pipe(map(questions => ({ ...target, exists: questions.length > 0 }))))).pipe(
      switchMap(configurations => {
        const writes: Observable<QuestionnaireSaveResult>[] = [];
        plans.forEach(planCreation => [false, true].forEach(exists => {
          const officeIds = configurations.filter(item => item.planCreation === planCreation && item.exists === exists)
            .map(item => item.officeId);
          if (!officeIds.length) { return; }
          const payload = { ...body, officeIds, planCreation };
          const domainParams = body.domain ? { domain: body.domain } : {};
           const request = exists ? this.updateQuestionsData(payload, domainParams) : this.submitQuestionsData(payload, domainParams);
          writes.push(request.pipe(
            map(() => ({ officeIds, planCreation, success: true } as QuestionnaireSaveResult)),
            catchError(error => of({ officeIds, planCreation, success: false, error } as QuestionnaireSaveResult))
          ));
        }));
        return concat(...writes).pipe(toArray());
      })
    );
  }
}
